import clsx from "clsx";
import CommentThreadPanel from "components/CommentThreadPanel";
import ExcelTable from "components/ExcelTable";
import { bool, func } from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { buildItemRefKey, fetchOutlineCounts } from "slices/comments";
import {
  setSelectedIndex,
  updateOutlineSections,
} from "slices/proposalOutline/outlineSlice";
import {
  selectAreAllSectionsLinked,
  selectIsEditing,
  selectOutlineId,
  selectProposalOutlineSections,
  selectSelectedIndex,
  selectSelectedRowIds,
} from "slices/proposalOutline/selectors";
import isNullish from "utils/isNullish";
import { isDropAllowedForLinkedDocument } from "utils/outlineDropValidation";

import { checkboxColumn, createColumns } from "./columns";
import CombinationDocumentChip from "./components/CombinationDocumentChip";
import OutlineTableHeader from "./components/OutlineTableHeader";
import TraceDrawer from "./components/TraceDrawer";
import { COVERAGE_TAB, SECTIONS_TAB } from "./constants";
import CoverageTab from "./CoverageTab";
import OutlineActions from "./OutlineActions";
import classes from "./OutlineTable.module.scss";
import OutlineTableContext from "./OutlineTableContext";
import { getExpandedRowModel } from "./utils";

const OutlineTable = ({
  onSubmitRegenerate,
  llmAvailable = true,
  isArchived = false,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isEditing = useSelector(selectIsEditing);
  const sections = useSelector(selectProposalOutlineSections);
  const isCombinedDocument = useSelector(selectAreAllSectionsLinked);
  const selectedIndex = useSelector(selectSelectedIndex);
  const selectedRowIds = useSelector(selectSelectedRowIds);
  const outlineId = useSelector(selectOutlineId);
  const excelTableRef = useRef(null);

  const [hoveredRow, setHoveredRow] = useState({ documentId: null, rowId: null });
  const [activeTab, setActiveTab] = useState(SECTIONS_TAB);
  const [traceSectionId, setTraceSectionId] = useState(null);
  const [drawerSectionId, setDrawerSectionId] = useState(null);
  const [commentSectionId, setCommentSectionId] = useState(null);
  const traceTriggerRef = useRef(null);
  const commentsTriggerRef = useRef(null);

  const isSectionsTab = activeTab === SECTIONS_TAB;
  const scrollToOutlineItemId = location.state?.outlineItemId;

  useEffect(() => {
    if (isNullish(scrollToOutlineItemId)) {
      return;
    }
    excelTableRef.current?.focusByCellId(scrollToOutlineItemId, "title");
  }, [scrollToOutlineItemId]);

  useEffect(() => {
    if (isNullish(outlineId)) {
      return;
    }
    dispatch(fetchOutlineCounts({ outlineId }));
  }, [outlineId, dispatch]);

  const onTraceSection = useCallback((sectionId, trigger) => {
    traceTriggerRef.current = trigger || null;
    setDrawerSectionId(sectionId);
  }, []);

  // The badge can be scrolled out of the virtualised table by the time the drawer
  // closes, in which case focusing a detached node is a harmless no-op.
  const onCloseDrawer = useCallback(() => {
    setDrawerSectionId(null);
    traceTriggerRef.current?.focus();
    traceTriggerRef.current = null;
  }, []);

  const onCommentSection = useCallback((sectionId, trigger) => {
    commentsTriggerRef.current = trigger || null;
    setCommentSectionId(sectionId);
  }, []);

  const onCloseComments = useCallback(() => {
    setCommentSectionId(null);
    commentsTriggerRef.current?.focus();
    commentsTriggerRef.current = null;
  }, []);

  const onOpenInCoverage = useCallback((sectionId) => {
    setDrawerSectionId(null);
    traceTriggerRef.current = null;
    setTraceSectionId(sectionId);
    setActiveTab(COVERAGE_TAB);
  }, []);

  const getIsRowExpanded = (row) => {
    const table = excelTableRef?.current?.table;
    const state = table?.getState?.();

    if (!state) {
      return true;
    }

    const { expanded } = state;
    const isExpandAll = expanded === true;

    const nextRow = sections[row.index + 1];
    const canExpand = nextRow?.level > row.original.level;
    if (!canExpand) {
      return false;
    }

    const rowId = row.id;
    return isExpandAll || !!expanded?.[rowId];
  };

  const getRowCanExpand = (row) => {
    const nextRow = sections[row.index + 1];
    return (nextRow?.level ?? -Infinity) > (row.original.level ?? -Infinity);
  };

  const getTable = useCallback(() => excelTableRef.current?.table, []);

  const columns = useMemo(() => {
    const cols = createColumns(isArchived, onTraceSection, getTable, isEditing, onCommentSection);
    return isEditing ? [checkboxColumn, ...cols] : cols;
  }, [isArchived, isEditing, onTraceSection, getTable, onCommentSection]);

  const getCellDepth = (row) => Math.max(0, row.original.level - 1);

  const isRowDragDisabled = useCallback(
    () => selectedRowIds.length > 1,
    [selectedRowIds]
  );

  const validateDrop = useCallback(
    (sourceRow, targetRow) =>
      isDropAllowedForLinkedDocument(sections, sourceRow, targetRow),
    [sections]
  );

  const onCellChangeFinished = (updated) => {
    dispatch(updateOutlineSections(updated));
  };

  const onDragFinished = (sortedSections) => {
    dispatch(updateOutlineSections(sortedSections));
  };

  const onRowClick = (_, __, index) => {
    dispatch(setSelectedIndex(index));
  };

  const onRowMouseEnter = (_, row) => {
    const { nextgenDocumentId } = row?.original || {};
    setHoveredRow({ documentId: nextgenDocumentId, rowId: row?.id });
  };

  const onRowMouseLeave = () => {
    setHoveredRow({ documentId: null, rowId: null });
  };

  const onSelectTab = useCallback((tab) => {
    setDrawerSectionId(null);
    traceTriggerRef.current = null;
    setCommentSectionId(null);
    commentsTriggerRef.current = null;
    setActiveTab(tab);
  }, []);

  return (
    <div className={classes["outline_table"]}>
      {isCombinedDocument && isSectionsTab && <CombinationDocumentChip />}

      <OutlineTableHeader
        activeTab={activeTab}
        onSelectTab={onSelectTab}
        onSubmitRegenerate={onSubmitRegenerate}
        llmAvailable={llmAvailable}
        isArchived={isArchived}
        tabs={[
          {
            content: "Outline",
            value: SECTIONS_TAB,
          },
          {
            content: "Coverage",
            value: COVERAGE_TAB,
          },
        ]}
      />

      {isSectionsTab ? (
        <>
          {!isArchived && <OutlineActions ref={excelTableRef} />}
          <div className={classes["outline_table__table_wrapper"]}>
            <OutlineTableContext.Provider value={hoveredRow}>
              <ExcelTable
                cellClassName={{
                  [classes["outline_table__table__row__cell"]]: isEditing,
                }}
                className={classes["outline_table__table"]}
                draggableColumnIndex={0}
                expandableColumnIndex={isEditing ? 1 : 0}
                headerCellClassName={clsx({
                  [classes["outline_table__table__row__header_cell"]]:
                    isEditing,
                })}
                indentAtColumnIndex={isEditing ? 1 : 0}
                columns={columns}
                data={sections}
                defaultSortColumnIndex={-1}
                expandAllSubRows
                getCellDepth={getCellDepth}
                getExpandedRowModel={getExpandedRowModel}
                getIsRowExpanded={getIsRowExpanded}
                getRowCanExpand={getRowCanExpand}
                isDraggable
                isEditing={isEditing}
                isRowDragDisabled={isRowDragDisabled}
                validateDragOver={validateDrop}
                validateDrop={validateDrop}
                isVirtualized
                onCellChangeFinished={onCellChangeFinished}
                onDragFinished={onDragFinished}
                onRowClick={onRowClick}
                onRowMouseEnter={onRowMouseEnter}
                onRowMouseLeave={onRowMouseLeave}
                ref={excelTableRef}
                rowClassName={classes["outline_table__table__row"]}
                selectedIndex={selectedIndex}
                subRowKey="sections"
              />
            </OutlineTableContext.Provider>
          </div>
        </>
      ) : (
        <CoverageTab traceSectionId={traceSectionId} />
      )}
      {drawerSectionId !== null && (
        <TraceDrawer
          onClose={onCloseDrawer}
          onOpenInCoverage={onOpenInCoverage}
          sectionId={drawerSectionId}
        />
      )}
      {commentSectionId !== null && (
        <CommentThreadPanel
          surface="OUTLINE_SECTION"
          containerId={commentSectionId}
          itemRef={{ outlineItemId: commentSectionId }}
          itemRefKey={buildItemRefKey("OUTLINE_SECTION", {
            outlineItemId: commentSectionId,
          })}
          triggerRef={commentsTriggerRef}
          onClose={onCloseComments}
        />
      )}
    </div>
  );
};

OutlineTable.propTypes = {
  isArchived: bool,
  llmAvailable: bool,
  onSubmitRegenerate: func,
};

export default OutlineTable;
